/**
 * Apply the deployment brand to the document (WS-A §2.8).
 *
 * Ported from `site-admin-ui/src/branding/cpApplyBranding.ts` — a copy into a new package,
 * never a shared import (D-16). Colours are the contract's HEX strings; `setProperty` of an
 * invalid value is a no-op, so there is no CSS-injection surface here.
 *
 * ⚠️ ONE BEHAVIOURAL DIVERGENCE from the origin, and it is a fix. The origin comments that
 * "the CP console is dark-only (`<html class="dark">`), so the DARK accent values win". That
 * premise is false in this package: `index.html` deliberately omits `class="dark"` and
 * polarity is resolved at runtime (D-18, `src/theme/polarity.ts`), so preferring the dark
 * accent unconditionally would paint a dark-tuned accent onto a light surface. Here the
 * resolved polarity chooses, and the brand's light values are used in light mode.
 */
import type { DeploymentBranding } from '@zion/sdk-cp-dp-contracts';
import type { ResolvedPolarity } from '@zion/sdk-contracts';

/**
 * The tab title on a deployment that supplies no brand.
 *
 * ⛔ `'Zion CP Admin'`, NOT the sibling's `'Console'`, and the two consoles run side by side on
 * one origin during the overlap. An operator with both open needs to read the tab and know which
 * is which; two tabs both saying "Console" is the failure.
 *
 * ⚠️ It must AGREE with `index.html`'s static `<title>`. That literal is what the browser shows
 * before the async branding load settles, and this value is what replaces it a moment later — so a
 * mismatch is a visible flicker from one name to another on every cold load. Two places now carry
 * the name, so `branding.test.tsx` asserts they are byte-identical rather than trusting that
 * whoever edits one remembers the other.
 */
export const CP_NEUTRAL_PRODUCT_NAME = 'Zion CP Admin';

/**
 * The favicon on a deployment that supplies no brand: the console's shield (the rail's own
 * `Shield` mark from `SectionHeader.tsx`) as a white outline on a rounded square in the platform
 * indigo. The square fills the canvas and the glyph is a stroke, so the tab reads like the DP
 * shell's bolt (Konrad, 2026-10-05: the first cut was a small filled silhouette on a smaller
 * square, and the tab showed the browser's globe before that).
 *
 * ⚠️ It must stay BYTE-IDENTICAL to the static `<link rel="icon">` in `index.html`. That element
 * is what the browser shows before the async branding load settles, and this value is what
 * `setFavicon` writes a moment later; `branding.test.tsx` asserts the two agree.
 */
export const CP_NEUTRAL_FAVICON_HREF =
  "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'>" +
  "<rect x='0' y='0' width='32' height='32' rx='8' fill='%235b5ef1'/>" +
  "<path transform='translate(5.8 5.8) scale(0.85)' fill='none' stroke='white' stroke-width='3.15' stroke-linecap='round' stroke-linejoin='round' " +
  "d='M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z'/></svg>";

function setVar(element: HTMLElement, name: string, value: string | null | undefined): void {
  if (value) element.style.setProperty(name, value);
  else element.style.removeProperty(name);
}

/**
 * Point the favicon at `href`.
 *
 * ⛔ The href is base-prefixed when the brand supplies a ROOT-RELATIVE path, or it 404s
 * under the console's base path. An absolute URL is passed through — a deployment pointing at a CDN has
 * already chosen its origin. The origin package does not need this because it is served at
 * the root; that is the whole reason this branch exists here. A `data:` href, the neutral
 * fallback, takes the absolute arm.
 */
function setFavicon(href: string, basePrefix: string): void {
  const existing = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  const link = existing ?? document.createElement('link');
  if (!existing) {
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = href.startsWith('/') ? `${basePrefix}${href}` : href;
}

export interface CpApplyBrandingOptions {
  /** Which polarity is on `<html>` right now — decides which accent pair wins. */
  readonly resolved: ResolvedPolarity;
  /** The console's base path, prepended to a ROOT-RELATIVE favicon. ⛔ `''` since the root
   *  cutover, and BOTH ternary arms in `setFavicon` stay live at `''` — `/a.png` and
   *  `https://cdn…/a.png` are still different cases — which is why this parameter survived. */
  readonly basePrefix: string;
}

export function cpApplyBranding(
  brand: DeploymentBranding | null,
  { resolved, basePrefix }: CpApplyBrandingOptions,
): void {
  const element = document.documentElement;
  const dark = resolved === 'dark';
  setVar(
    element,
    '--color-accent',
    (dark ? brand?.primaryColorDark ?? brand?.primaryColor : brand?.primaryColor) ?? null,
  );
  setVar(
    element,
    '--color-accent-secondary',
    (dark ? brand?.accentSecondaryDark ?? brand?.accentSecondary : brand?.accentSecondary) ?? null,
  );
  document.title = brand?.consoleName ?? brand?.productName ?? CP_NEUTRAL_PRODUCT_NAME;
  setFavicon(brand?.faviconUrl ?? CP_NEUTRAL_FAVICON_HREF, basePrefix);
}
